import AppModule from '@app/lib/AppModule';
import { AuthEvents } from '@modules/AuthModule/types';
import { tasksState } from './state';
import {
  AcceptedOnDevice,
  CreateTaskOptions,
  SkipClassificationReason,
  TaskCustomer,
  TaskState,
  TaskTypeDefinition as TaskDefinition,
} from './types';
import Task from './Task';
import appManager from '@app/appManager';
import getInteractionClassificationData from './lib/getInteractionClassificationData';
import {
  InteractionClassificationStatus,
  InteractionContactInput,
  InteractionStatus,
} from '@api/generated/graphql';
import { showCloseAndClassifyErrorToast } from '@modules/ToastModule/commonToasts/commonToasts';
import { PcsConversationFetchedEvent } from '@modules/SoftPhoneModule/types';
import handlePcsConversationFetched from '../InboxModule/events/handlePcsConversationFetched';
import FullStoryModule from '@modules/FullStoryModule';
import { readPersistedTasks } from './lib/taskPersistence';
import DiagnosticsModule, {
  MetricNames,
  MetricTags,
  OperationNames,
  ClassificationErrorTypes,
} from '@modules/DiagnosticsModule';
import { escapeRegExp } from 'lodash';
import { matchesNetworkTestDial } from '@modules/SoftPhoneModule/lib/networkTestDial';

// THIS MODULE IS A WORK IN-PROGRESS A WILL BE REFACTORED
// SEE DAVEW BEFORE MAKING MAJOR CHANGES

function getTaskEvents(): string[] {
  return [
    'task.pending',
    'task.offered',
    'task.rejected',
    'task.abandoned',
    'task.accepted',
    'task.wrapup',
    'task.completed',
    'task.timedout',
    'task.updated',
    'task.action.accept',
    'task.action.reject',
    'task.action.complete',
    'task.action.complete-classified',
    'task.action.unhold',
    'task.action.transfer',
  ];
}

export class TasksModule extends AppModule {
  override events = [...getTaskEvents()];

  private readonly taskDefinitions: Record<string, TaskDefinition> = {};

  override preload(): void {
    this.app.events.on('task.updated', () => {
      tasksState.version++;
    });

    this.app.events.on(AuthEvents.SIGNOUT, () => {
      this.handleClearTasks();
    });

    this.app.events.on<PcsConversationFetchedEvent>(
      'phone.pcs.conversation.fetched',
      (ev) => {
        handlePcsConversationFetched(
          ev.pcsConversation,
          ev.phoneStateUpdateEvent,
          this.app.logger,
        );
      },
    );
  }

  override load(): void {
    // load our saved tasks from localStorage
    tasksState.tasks = readPersistedTasks(this.taskDefinitions) || {};
  }

  public createTask(options: CreateTaskOptions): Task {
    this.app.logger.info('tasks:create', options);
    const newTask = new Task(options);
    tasksState.tasks[newTask.id] = newTask;
    return tasksState.tasks[newTask.id];
  }

  public registerTaskDefinition(type: TaskDefinition): void {
    if (this.taskDefinitions[type.key]) {
      this.app.logger.warn(
        `Task type with key ${type.key} is already registered. Overwriting.`,
      );
    }
    this.taskDefinitions[type.key] = type;
    this.app.logger.info(`Task type registered: ${type.key}`, type);
  }

  public getTaskDefinitionFromType(type: string): TaskDefinition | undefined {
    const definitionKey = Object.keys(this.taskDefinitions).find((key) =>
      this.taskDefinitions[key].types.includes(type),
    );
    if (definitionKey) {
      return this.taskDefinitions[definitionKey];
    }
    return undefined;
  }

  public getTaskDefinitions(): Record<string, TaskDefinition> {
    return this.taskDefinitions;
  }

  public getTaskDefinition(key: string): TaskDefinition | undefined {
    return this.taskDefinitions[key];
  }

  /**
   * In order for us to populate the task with tenantId and the customer information like id and name, we need to create one before we place the call.
   * Once the outbound call is made, we will try to find the task with the same number and update the task with the sessionId.
   * Handled in `setOutboundCallToOffered`.
   */
  public createOutboundCallTask(
    outgoingNumber: string,
    displayName?: string,
    tenantId?: string,
    taskAttributes?: Record<string, any>,
  ): Task | undefined {
    if (matchesNetworkTestDial(outgoingNumber)) {
      return undefined;
    }
    return this.createTask({
      isInternal: true,
      id: `tmp-outbound-${outgoingNumber}`,
      displayName,
      direction: 'outbound',
      type: 'voice',
      state: TaskState.Pending,
      tenantId,
      attributes: taskAttributes,
      from: outgoingNumber,
    });
  }

  // This method is used to update the task id when the outbound call is made. Since we create it with
  // a temporary session id (`tmp-outbound`), we need to update it with the actual session id when the
  // call is placed.
  public updateTaskId(task: Task, newSessionId: string) {
    tasksState.tasks[newSessionId] = task;
    this.deleteTask(task.id);
    tasksState.tasks[newSessionId].id = newSessionId;
  }

  public setTaskState(sessionId: string, taskState: TaskState) {
    const task = tasksState.tasks[sessionId];
    if (!task) {
      this.app.logger.error('task not found', sessionId);
      return;
    }
    task.state = taskState;
  }

  public setTaskAttributes(taskId: string, attributes: Record<string, any>) {
    const task = tasksState.tasks[taskId];
    if (!task) {
      throw new Error(`Task not found: ${taskId}`);
    }
    task.attributes = attributes;
  }

  public updateTaskAttributes(taskId: string, attributes: Record<string, any>) {
    const task = tasksState.tasks[taskId];
    if (!task) {
      throw new Error(`Task not found: ${taskId}`);
    }
    task.attributes = {
      ...task.attributes,
      ...attributes,
    };
  }

  public updateTaskIsStateActive(taskId: string, isStateActive: boolean) {
    const task = tasksState.tasks[taskId];
    if (!task) {
      throw new Error(`Task not found: ${taskId}`);
    }
    task.isStateActive = isStateActive;
  }

  public setTaskAttribute(taskId: string, key: string, value: any) {
    const task = tasksState.tasks[taskId];
    if (!task) {
      throw new Error(`Task not found: ${taskId}`);
    }
    this.setTaskAttributes(taskId, {
      ...task.attributes,
      [key]: value,
    });
  }

  public setTaskDisplayName(taskId: string, displayName: string | undefined) {
    const task = tasksState.tasks[taskId];
    if (!task) {
      throw new Error(`Task not found: ${taskId}`);
    }
    task.displayName = displayName;
  }

  public setCustomerOnTask(taskId: string, customer: TaskCustomer): void {
    const task = tasksState.tasks[taskId];
    if (!task) {
      this.app.logger.error('task not found', taskId);
      return;
    }
    task.setCustomer(customer);
  }

  /**
   * Writes contacts to the task and emits `task.updated` (field='contacts').
   * Returns `false` when the task is no longer in state — callers can use the
   * return value to fall back to a direct interaction write when the task has
   * already been disposed (e.g. post-transfer technician attribution).
   * Whether a missing task is an error is the consumer's call.
   */
  public setContactsOnTask(
    taskId: string,
    contacts: InteractionContactInput[] | undefined,
  ): boolean {
    const task = tasksState.tasks[taskId];
    if (!task) {
      return false;
    }
    task.setContacts(contacts);
    return true;
  }

  public setTaskComplete(taskId: string) {
    const task = tasksState.tasks[taskId];
    if (!task) {
      console.error('task not found', taskId);
      return;
    }
    task.state = TaskState.Completed;
    this.deleteTask(taskId);
  }

  public deleteTask(taskId: string) {
    delete tasksState.tasks[taskId];
  }

  public actionTask(
    taskId: string,
    action:
      | 'accept'
      | 'reject'
      | 'complete'
      | 'complete-classified'
      | 'unhold'
      | 'transfer',
  ) {
    const task = tasksState.tasks[taskId];
    if (!task) {
      console.error('task not found', taskId);
      return;
    }
    this.app.events.emit(`task.action.${action}`, task);
  }

  public getTask(taskId: string): Task | null {
    return tasksState.tasks[taskId] || null;
  }

  public getTaskByCallId(callId: string): Task | undefined {
    return Object.values(tasksState.tasks).find(
      (task) => task.attributes?.callId === callId,
    );
  }

  public getTasks(): Task[] {
    return Object.values(tasksState.tasks);
  }

  public hasActiveTaskAcceptedByMobile(): boolean {
    return this.getTasks().some(
      (task) =>
        task.state === TaskState.Accepted &&
        task.attributes.acceptedOn === AcceptedOnDevice.OtherDevice,
    );
  }

  /** Finds the most recently created pending outbound call task matching the given phone number, or returns null if none exists. */
  public findOutboundCallTask(outgoingNumber: string): Task | null {
    const outboundTasksToNumber = Object.values(tasksState.tasks)
      .filter(
        (task) =>
          task.state === TaskState.Pending &&
          task.direction === 'outbound' &&
          task.from.includes(outgoingNumber),
      )
      .sort((a, b) => a.startedAt.getTime() - b.startedAt.getTime());

    return outboundTasksToNumber.pop() || null;
  }

  public findOutboundCallTaskByRemoteCallId(remoteCallId: string): Task | null {
    const outboundTask = Object.values(tasksState.tasks).find(
      (task) =>
        task.direction === 'outbound' &&
        task.attributes?.remoteCallId === remoteCallId,
    );

    return outboundTask ?? null;
  }

  /**
   * Retrieves a connecting call task by matching either the remote or local identity.
   *
   * @param remoteIdentity - The remote identity to match against the task's `from` attribute.
   * @param localIdentity - The local identity to match against the task's `source` attribute.
   * @returns The matching `Task` object if found, otherwise `null`.
   */
  public getConnectingCallTaskByRemoteOrLocalIdentity(
    remoteIdentity: string,
    localIdentity: string,
  ): Task | null {
    const task = Object.values(tasksState.tasks)
      .filter((task) => task.attributes?.isConnecting)
      .find(
        (task) =>
          remoteIdentity.includes(task.from) ||
          task.attributes.source === localIdentity,
      );

    return task ?? null;
  }

  /**
   * Retrieves an offered task by the source address, excluding the task with the specified session SID.
   *
   * This function searches through the tasks in the `tasksState` and returns the task
   * that matches the given source address, while ensuring that the task is either in the offered or accepted state.
   *
   * @param {string} sourceAddress - The source address to match against the task's remote or local identity.
   * @returns {Task | null} - The most recent task that matches the source address and is not the specified session SID, or null if no such task is found.
   */
  public getOfferedOrAcceptedTaskBySourceAddress(
    sourceAddress: string,
  ): Task | null {
    const escapedSourceAddress = escapeRegExp(sourceAddress);

    const task = Object.values(tasksState.tasks).find((task) => {
      const remoteIdentity = task.attributes?.remoteIdentity;
      const localIdentity = task.attributes?.localIdentity;
      return (
        (task.state === TaskState.Offered ||
          task.state === TaskState.Accepted) &&
        ((remoteIdentity &&
          new RegExp(escapedSourceAddress, 'i').test(remoteIdentity)) ||
          localIdentity === sourceAddress)
      );
    });

    return task ?? null;
  }

  public async checkClassificationRequirement(
    taskId: string,
  ): Promise<SkipClassificationReason | 'NotReady' | 'NotEnded' | null | true> {
    const task = this.getTask(taskId);
    if (!task) {
      console.error('task not found for sessionId:', taskId);
      return null;
    }

    try {
      task.loading = true;

      const classificationData = await getInteractionClassificationData(
        {
          interactionId: task.attributes?.interactionId,
          channelId: task.id,
        },
        this.app.logger,
      );

      if (!classificationData?.classificationStatus) {
        this.app.logger.error('Classification status not available', taskId);

        // Track when classification status is null
        FullStoryModule.trackEvent('[CCP-Call] Classification Status Null', {
          taskId: task.id,
          tenantId: task.tenantId,
          interactionId: task.attributes?.interactionId,
        });

        DiagnosticsModule.incrementCounter(MetricNames.ERROR_TOTAL, {
          [MetricTags.OPERATION_NAME]: OperationNames.CLASSIFICATION,
          [MetricTags.ERROR_TYPE]: ClassificationErrorTypes.STATUS_NULL,
        });

        showCloseAndClassifyErrorToast();
        return null;
      }

      const { classificationStatus, completedAt, status } = classificationData;

      // Store completedAt in task attributes for use in classification
      this.setTaskAttribute(task.id, 'completedAt', completedAt);

      if (status === InteractionStatus.Completed) {
        this.app.logger.info(
          'Interaction is already completed, classification is not required',
          taskId,
        );
        return SkipClassificationReason.ClassificationCompleted;
      }

      if (
        classificationStatus === InteractionClassificationStatus.NotRequired
      ) {
        this.app.logger.info(
          'Classification not required based on status',
          taskId,
        );
        return SkipClassificationReason.NotRequired;
      }

      if (classificationStatus === InteractionClassificationStatus.Required) {
        this.app.logger.info('Classification required based on status', taskId);
        return true;
      }

      // Handle NotReady case
      if (classificationStatus === InteractionClassificationStatus.NotReady) {
        this.app.logger.info('Classification status is NotReady', taskId);

        if (!completedAt) {
          this.app.logger.info(
            'Call has not ended yet (completedAt is null)',
            taskId,
          );

          FullStoryModule.trackEvent(
            '[CCP-Call] Classification call not ended',
            {
              taskId: task.id,
              tenantId: task.tenantId,
            },
          );
          DiagnosticsModule.incrementCounter(MetricNames.ERROR_TOTAL, {
            [MetricTags.OPERATION_NAME]: OperationNames.CLASSIFICATION,
            [MetricTags.ERROR_TYPE]: ClassificationErrorTypes.NOT_ENDED,
          });

          return 'NotEnded';
        }

        FullStoryModule.trackEvent('[CCP-Call] Classification call not ready', {
          taskId: task.id,
          tenantId: task.tenantId,
        });
        DiagnosticsModule.incrementCounter(MetricNames.ERROR_TOTAL, {
          [MetricTags.OPERATION_NAME]: OperationNames.CLASSIFICATION,
          [MetricTags.ERROR_TYPE]: ClassificationErrorTypes.NOT_READY,
        });

        return 'NotReady';
      }

      showCloseAndClassifyErrorToast();
      this.app.logger.error('unhandled classification', {
        task,
        classificationStatus,
        completedAt,
      });

      // Track metric for classification error
      DiagnosticsModule.incrementCounter(MetricNames.ERROR_TOTAL, {
        [MetricTags.OPERATION_NAME]: OperationNames.CLASSIFICATION,
        [MetricTags.ERROR_TYPE]:
          ClassificationErrorTypes.UNHANDLED_CLASSIFICATION,
      });

      return null;
    } catch (error) {
      this.app.logger.error('Error in checkClassificationRequirement', error);

      // Track metric for classification error
      DiagnosticsModule.incrementCounter(MetricNames.ERROR_TOTAL, {
        [MetricTags.OPERATION_NAME]: OperationNames.CLASSIFICATION,
        [MetricTags.ERROR_TYPE]: ClassificationErrorTypes.EXCEPTION,
      });

      return null;
    } finally {
      task.loading = false;
    }
  }

  public setTaskLoading = (taskId: string, loading: boolean) => {
    const task = tasksState.tasks[taskId];
    if (!task) {
      throw new Error(`Task not found: ${taskId}`);
    }
    task.loading = loading;
  };

  private handleClearTasks = () => {
    tasksState.tasks = {};
  };
}

export default new TasksModule(appManager.events, appManager.logger('Tasks'));
